import { useCallback, useEffect, useRef, useState } from "react"

/** Everything a text input needs, ready to spread. */
export interface DebouncedField {
  value: string
  onChange: (event: { target: { value: string } }) => void
  onBlur: () => void
}

interface Options {
  /** The value as Firestore currently has it. */
  value: string
  /** Called with the text to save. Fire-and-forget — see `useFirestoreWrite`. */
  onCommit: (value: string) => void
  /** Quiet time before saving. */
  delayMs?: number
}

/**
 * Local text draft with debounced Firestore commits.
 * Commits on blur and unmount, and ignores remote changes while editing
 * so the user's cursor is not disturbed.
 *
 * Last writer wins when multiple users edit the same field.
 */

export function useDebouncedField({ value, onCommit, delayMs = 400 }: Options): DebouncedField {
  const [draft, setDraft] = useState<string | null>(null)

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Text not yet committed. */
  const uncommitted = useRef<string | null>(null)
  /** Latest callback for the unmount flush. */
  const commit = useRef(onCommit)
  /** The remote value this field has already reconciled against. */
  const seen = useRef(value)

  useEffect(() => {
    commit.current = onCommit
  }, [onCommit])

  /*
   * The draft is handed back only once a NEW remote value arrives — dropping it
   * the moment the write goes out would repaint the box with the text Firestore
   * still holds, so every blur flashed the old answer until the echo landed.
   * Text still waiting to be committed keeps the draft: that edit is newer than
   * whatever the listener just delivered.
   */
  useEffect(() => {
    if (value === seen.current) return
    seen.current = value
    if (uncommitted.current === null) setDraft(null)
  }, [value])

  const flush = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const text = uncommitted.current
    uncommitted.current = null
    if (text !== null) commit.current(text)
  }, [])

  useEffect(() => flush, [flush])

  const onChange = useCallback(
    (event: { target: { value: string } }) => {
      const next = event.target.value
      setDraft(next)
      uncommitted.current = next

      if (timer.current !== null) clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        timer.current = null
        const text = uncommitted.current
        uncommitted.current = null
        if (text !== null) commit.current(text)
      }, delayMs)
    },
    [delayMs]
  )

  const onBlur = useCallback(() => {
    flush()
  }, [flush])

  return { value: draft ?? value, onChange, onBlur }
}
